class CartItems extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cartDrawer = document.querySelector('cart-drawer');
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

    this.isCartPage = !!document.body.classList.contains('template-cart');

    this.options = {
      showCartUpsell: false,
    };

    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    window.addEventListener('quantity-change', this.debouncedOnChange);
  }

  disconnectedCallback() {
    window.removeEventListener('quantity-change', this.debouncedOnChange);
  }

  onChange(event) {
    if (!event.target.closest('[data-cart-item]')) return;

    const { key, quantity } = event.detail;
    this.updateQuantity(key, quantity, event.target.closest('[data-cart-item]'));
  }

  async updateQuantity(key, quantity, el) {
    this.enableLoading();

    const body = JSON.stringify({
      id: key,
      quantity,
      sections: this.sectionsToFetch,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        ...{ body },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        debug() && console.error(errorBody);

        throw new Error(response.status);
      }

      this.parsedState = await response.json();

      // Item was removed
      if (+quantity === 0) {
        // animate the item to be removed
        el.querySelector('cart-remove-button')?.classList.add('hidden');
        el.querySelector('[data-cart-item-wrapper]').style.gridTemplateRows = '0fr';

        // re-add logic
        el.querySelector('[data-cart-item-wrapper]').addEventListener('transitionend', () => {
          el.closest('li').remove();
        });
      }
      // Item qty was updated
      else {
        // update the item key because it may have changed
        const currentItemKey = this.parsedState.items.find(item => item.id === +el.dataset.cartItemId);

        el.dataset.cartItemKey = currentItemKey.key;
      }

      this.updateContent(this.parsedState, el);
      this.classList.toggle('cart-is-empty', this.parsedState.item_count === 0);
    }
    catch (error) {
      debug() && console.error('Error updating the item.', error);

      document.querySelector('[data-cart-loader-active="show"]')?.classList.add('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList.remove('hidden');

      document.getElementById('cart-errors').textContent = window.cartStrings.error;

      if (error.toString().includes('422')) {
        this.updateAfterError(key);
      }
    }
    finally {
      this.disableLoading();
    }
  }

  async updateContent(parsedState, el) {
    let sectionsToRender = [
      ...this.getCartSectionsToRender(),
    ];
    if (this.isCartPage && this.options.showCartUpsell) {
      sectionsToRender = [
        ...sectionsToRender,
        ...this.getCartRelatedSection(),
      ];
    }
    else if (this.cartDrawer && this.cartDrawer.options.showCartDrawerUpsell) {
      sectionsToRender = [
        ...sectionsToRender,
        ...this.getDrawerSectionsToRender(),
      ];
    }

    sectionsToRender.forEach((section, index) => {
      if (section?.selector) {

        const selector = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

        if (selector && section.selector && parsedState.sections?.[section.section]) {
          selector.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }

        if (section.selector === '.js-cart-related-items-swiper') {
          // init the swiper after a change
          setTimeout(() => {
            selector.querySelector('swiper-slider').init();
          }, 500);
        }

        if (index === sectionsToRender.length - 1) {
          setTimeout(() => {
            window.initDialogs(document.querySelector('cart-drawer'));
          }, 200);
        }
      }
    });

    if (el) el.focus();
  }

  async updateAfterError(key) {
    const id = key.split(':')[0];
    if (!id) return;

    // Do a new fetch to the cart so you get the correct values of the item that had errors
    const body = JSON.stringify({
      sections: this.sectionsToFetch,
      sections_url: window.location.pathname,
    });

    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      ...{ body },
    });

    const parsedState = await response.json();
    const lineItemWithError = parsedState.items.filter(item => item.id == id)[0];

    // show the error
    if (lineItemWithError) {
      this.showLineItemError(id, lineItemWithError.quantity);
    }
    // update the cart because the erroneous item is no longer in the cart
    else {
      this.updateContent(parsedState);
      this.classList.toggle('cart-is-empty', parsedState.item_count === 0);
    }
  }

  // show error text for the line item
  showLineItemError(id, quantity) {
    document.getElementById(`Line-item-error-${id}`).querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace('[quantity]', quantity);
    document.getElementById(`Quantity-${id}`).value = quantity;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  get sectionsToFetch() {
    const sections = [
      ...this.getCartSectionsToRender().map(section => section.section),
    ];
    if (this.isCartPage && this.options.showCartUpsell) {
      sections.push(
        ...this.getCartRelatedSection().map(section => section.section),
      );
    }
    else if (this.cartDrawer?.options.showCartDrawerUpsell) {
      sections.push(
        ...this.getDrawerSectionsToRender().map(section => section.section),
      );
    }
    return [
      ...new Set(sections),
    ];
  }

  getCartSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-cart-item-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-cart-footer-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'theme-cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
    ];
  }

  getDrawerSectionsToRender() {
    return [
      {
        id: 'cartDrawer',
        section: document.getElementById('cartDrawer').dataset.id,
        selector: '.js-cart-related-items-swiper',
      },
    ];
  }

  getCartRelatedSection() {
    return [
      {
        id: 'cartRelated',
        section: document.getElementById('cartRelated').dataset.id,
        selector: '.cart-related-items',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const htmlSelector = document.createRange().createContextualFragment(html).querySelector(selector);
    if (htmlSelector) {
      return htmlSelector.innerHTML;
    }
  }

  enableLoading() {
    document.querySelector('cart-items').classList.add('cart-items-is-loading');
    document.querySelector('[data-cart-loader-active="show"]')?.classList.remove('hidden');
    document.querySelector('[data-cart-loader-active="hidden"]')?.classList.add('hidden');

    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    document.querySelector('cart-items').classList.remove('cart-items-is-loading');
  }
}
if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();

      this.closest('cart-items').updateQuantity(event.currentTarget.dataset.cartItemKey, 0, event.target.closest('[data-cart-item]'));
    });
  }
}
if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartNote extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', debounce((event) => {
      const body = JSON.stringify( {
        note: event.target.value,
      });
      fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{
          body,
        },
      });
    }, 300),
    );
  }
}
if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
